import { env } from "@/config/env";
import { CLIENT_SUFFIX_HEADER } from "@/config/tenant";
import { TIMEOUT_MS } from "@/config/timeouts";
import { isNestEnvelope } from "./nest-envelope";
import { isValidSessionId } from "./session-id";

/**
 * Sessão de SERVIÇO para as leituras públicas do SSR.
 *
 * A nest exige `x-session-id` em toda rota, inclusive catálogo, e só aceita um
 * id que ela mesma emitiu (64 hex — `sessionIdValidator.validateIssued`). O
 * valor fixo que o `publicGet` mandava antes ("public-read") passou a ser 400.
 *
 * Como a validação é só de FORMATO (a sessão não precisa existir no banco), o
 * processo cria UMA sessão por tenant via `/auth/session/web` e a reutiliza
 * enquanto viver. Continua sendo um id constante por tenant — o que mantém o
 * payload anônimo (sem Bearer) e a chave do Data Cache estável dentro do pod.
 * O cache já era por pod (sem `cacheHandler` compartilhado), então um id por
 * pod não o fragmenta mais do que já é.
 *
 * Server-only por construção: quem chama é o ramo servidor do `publicGet`.
 */

const sessions = new Map<string, Promise<string>>();

/** Id de sessão de serviço do tenant, criado uma vez por processo. */
export function getServiceSessionId(clientSuffix: string): Promise<string> {
    let pending = sessions.get(clientSuffix);
    if (!pending) {
        pending = createServiceSession(clientSuffix).catch((error: unknown) => {
            // Falhou: a próxima leitura tenta de novo em vez de ficar presa.
            sessions.delete(clientSuffix);
            throw error;
        });
        sessions.set(clientSuffix, pending);
    }
    return pending;
}

/**
 * Descarta a sessão do tenant — para quando a nest responde
 * `400 Session ID inválido` (ex.: regra de formato mudou num deploy).
 */
export function invalidateServiceSession(clientSuffix: string): void {
    sessions.delete(clientSuffix);
}

async function createServiceSession(clientSuffix: string): Promise<string> {
    const response = await fetch(`${env.apiUrl}/api/v1/auth/session/web`, {
        method: "POST",
        headers: {
            "content-type": "application/json",
            [CLIENT_SUFFIX_HEADER]: clientSuffix,
        },
        body: JSON.stringify({
            deviceName: "ecommerce-ssr",
            systemName: "Linux",
            // A nest valida como IP; o SSR não tem visitante, é o próprio pod.
            customerIp: "127.0.0.1",
        }),
        cache: "no-store",
        signal: AbortSignal.timeout(TIMEOUT_MS.nestProxy),
    });

    const body: unknown = await response.json().catch(() => null);
    const data = (isNestEnvelope(body) ? body.data : body) as { sessionId?: unknown } | null;
    const sessionId = data?.sessionId;

    if (!response.ok || !isValidSessionId(sessionId)) {
        throw new Error(
            `Não foi possível criar a sessão de serviço do tenant ${clientSuffix} (HTTP ${response.status}).`
        );
    }
    return sessionId;
}
